import { KROT } from 'captcha-eu-sdk';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useState } from 'react';
import apiService from '@/services/apiService';

const CAPTCHA_PUBLIC_KEY = import.meta.env.VITE_CAPTCHA_PUBLIC_KEY;

/**
 * Resolve a captcha solution string for the "no PDF" submission. In e2e
 * (localStorage `__e2e__` flag) we skip the real KROT round-trip; the
 * backend bypasses captcha in that mode.
 */
export const getCaptchaSolution = async (): Promise<string> => {
	if (window.localStorage.getItem('__e2e__')) return '';
	try {
		const solution = await KROT.getSolution();
		return JSON.stringify(solution);
	} catch (error) {
		console.error('Captcha error:', error);
		throw new Error('Failed to get captcha solution', { cause: error });
	}
};

/**
 * Idempotent KROT setup. Flips `isCaptchaReady` to true once the SDK has
 * been primed with the public key so the caller can disable its CTA until
 * the challenge is actually solvable.
 */
export const useCaptchaSetup = (): { isCaptchaReady: boolean } => {
	const [isCaptchaReady, setIsCaptchaReady] = useState(false);

	useEffect(() => {
		KROT.setup(CAPTCHA_PUBLIC_KEY);
		setIsCaptchaReady(true);
	}, []);

	return { isCaptchaReady };
};

/**
 * Drop the customer into the existing "no policy" flow: captcha solve →
 * POST `/policy-upload-forms/submit` with no file → store access token →
 * identify in PostHog → hard-redirect to the server-issued URL that lands
 * the user on the modal-driven company/sector/wage/coverage entry.
 *
 * Shared between {@link Uploader} (dedicated skip button) and the bundled
 * Arbo variant of `/polis-niet-leesbaar`, where the same outcome is the
 * natural recovery from an unsupported bundled upload.
 */
export interface UseSkipPolicyUploadResult {
	isCaptchaReady: boolean;
	isProcessing: boolean;
	skip: () => Promise<void>;
}

export const useSkipPolicyUpload = (
	onError?: (error: unknown) => void
): UseSkipPolicyUploadResult => {
	const posthog = usePostHog();
	const { isCaptchaReady } = useCaptchaSetup();
	const [isProcessing, setIsProcessing] = useState(false);

	const skip = useCallback(async () => {
		setIsProcessing(true);
		try {
			const captchaSolution = await getCaptchaSolution();
			const response = await apiService.policy.submitWithoutFile(
				captchaSolution,
				window.location.href
			);
			// ``policy_upload_form_id`` + ``access_token`` are contract-required
			// on the backend; ``temp_redirect_url`` is populated only when the
			// server has a destination to send us to. Persist credentials as
			// soon as the required fields arrive so a stalled/unexpected
			// response without a redirect doesn't lose the token.
			if (!response.policy_upload_form_id || !response.access_token) {
				throw new Error('No policy upload form ID received');
			}
			localStorage.setItem(response.policy_upload_form_id, response.access_token);
			posthog?.identify(response.policy_upload_form_id);
			if (response.temp_redirect_url) {
				window.location.href = response.temp_redirect_url;
				return;
			}
			// Nothing to navigate to — release the spinner so the caller can
			// decide what to do next (e.g. surface an error banner via
			// ``onError`` if the caller wants to treat it as a failure).
			setIsProcessing(false);
		} catch (err) {
			console.error('Skip upload error:', err);
			setIsProcessing(false);
			onError?.(err);
		}
	}, [onError, posthog]);

	return { isCaptchaReady, isProcessing, skip };
};
